import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { registerBackHandler } from './useAndroidBackButton';
/**
 * Global scroll lock counter to track multiple simultaneous modals
 */
let scrollLockCount = 0;
let originalRootOverflow = '';
let originalBodyOverflow = '';

/**
 * Get the #root element (the actual scroll container since body is position:fixed).
 */
const getRootElement = (): HTMLElement | null => document.getElementById('root');

/**
 * useScrollLock Hook
 * 
 * Centralized scroll lock management with reference counting.
 * Prevents race conditions when multiple modals are opened/closed.
 * Targets both body and #root since body is position:fixed and
 * #root is the actual scroll container.
 * Includes iOS keyboard resilience: resets scroll position on unlock
 * to prevent the page from staying stuck behind the address bar.
 * 
 * @param isLocked - Whether to lock the scroll
 * 
 * @example
 * // In a modal component
 * useScrollLock(isOpen);
 */
export const useScrollLock = (isLocked: boolean, onClose?: () => void) => {
    useEffect(() => {
        let removeBackHandler: (() => void) | undefined;

        if (isLocked) {
            // Hardware back button integration for modals
            if (onClose && Capacitor.getPlatform() === 'android') {
                removeBackHandler = registerBackHandler(() => {
                    onClose();
                    return true;
                });
            }

            const root = getRootElement();

            // Store original overflow values on first lock
            if (scrollLockCount === 0) {
                originalBodyOverflow = document.body.style.overflow || '';
                originalRootOverflow = root?.style.overflow || '';
            }
            scrollLockCount++;

            // Lock both body and #root scroll
            document.body.style.overflow = 'hidden';
            if (root) {
                root.style.overflow = 'hidden';
            }

            return () => {
                scrollLockCount = Math.max(0, scrollLockCount - 1);
                const rootEl = getRootElement();

                // Only restore overflow when all locks are released
                if (scrollLockCount === 0) {
                    document.body.style.overflow = originalBodyOverflow;
                    if (rootEl) {
                        rootEl.style.overflow = originalRootOverflow;
                    }
                }

                if (removeBackHandler) {
                    removeBackHandler();
                }
            };
        }
    }, [isLocked, onClose]);
};
